/* eslint-disable no-else-return */
import React, { useState, useEffect, useContext } from 'react';
import { useIntl } from 'react-intl';
import moment from 'moment-timezone';
import TextField from '@material-ui/core/TextField';
import {
  ConfirmModal,
  DATE_PICKER_SUGGEST_BOX_WIDTH_PX,
  DATE_TIME_PICKER_SUGGEST_BOX_WIDTH_PX,
  DialogBox,
} from '@mummssoftware/common-ui';
import { VFGlobalContext } from '@mummssoftware/global-context';
import { emptyText, Hospice, CPF, error } from '@mummssoftware/utils/constants';
import {
  getOverrideAgency,
  NoAttendingPhys,
  isUserDoctorRoleGroup,
  isHomeFacility,
} from '@mummssoftware/utils/utils';
import { GlobalContext } from '@mummssoftware/global-context/GlobalContext';
import {
  generateNewFrequency,
  findMaxEditableDate,
} from '@mummssoftware/utils/frequencies/frequency-utils';
import {
  offsetDate,
  toSiteTz,
  getMaxOrderDate,
  getUserCredentials,
  getLocalOrderTime,
} from '@mummssoftware/utils/orders/orders-utils';
import {
  OrdersGlobalContext,
  SET_DISPLAY_CONFIRMATION,
  SET_ORDER_STATEMENT,
} from '@mummssoftware/global-context/OrdersGlobalContext';
import {
  formatLocDateMMDDYYYY,
  formatAdmitPostDate,
  formatDateYYYYMMDD,
  formatDateTimeMMDD,
} from '@mummssoftware/utils/formatters';
import { dateToMoment } from '@mummssoftware/utils/dates';
import {
  ADM,
  LOC,
  FC,
  OTH,
  MMD,
  PSO,
} from '@mummssoftware/utils/orders/orders-constants';
import { IDGGlobalContext } from '@mummssoftware/idg';
import {
  LOCPicker,
  FacilityPicker,
  EffDatePicker,
  EffDateTimePicker,
} from '../Components/components';
import {
  SET_EFFECTIVE_DATE,
  SET_LOC_TYPE,
  GET_LOC_TYPES,
  SET_ORDER_DISABLED,
  SET_FACILITY_OPTIONS,
  SET_FACILITY_TYPE,
} from '../Context/constants';
import { ProgressNotesContext } from '../Context/ProgressNotesContext';
import { useStyles } from '../style/style';
import { Text, ErrorText } from '../../components/layout/WrapperDiv';
import { Context } from '../../context';
import defaults from '../../translations/en.json';
import {
  levelOfCaresMapping,
  lOCLongMapping,
  lOCStatements,
} from './constants';
import { isOverrideOrder, homeFacilityFirst } from './utils';

const GenericStatement = ({ setShowLoader }: any) => {
  const {
    admitPatient,
    saveLOC,
    saveCarePlanFrequency,
    pim,
    currentUser,
    agency,
    savedApproval,
    siteId,
  } = useContext(Context);
  const {
    dispatch,
    locTypes,
    orderCreated,
    orderDisabled,
    approvalDateError,
    rgHash,
    attendingPhysician,
    pecosPhysicians,
    pickerDialogHeight,
    isOverrideVerifOrderAgency,
    handleGenericOrder,
    handleSendApproval,
    isPhysicianRoleGroup,
    resetWorkflow,
  } = useContext(ProgressNotesContext);
  const {
    orderType,
    admitPhysician,
    verbalDate,
    createOrder,
    orderStatement,
    dispatchOrders,
  } = useContext(OrdersGlobalContext);
  const globalContext = useContext(GlobalContext);
  const { patientDateRestrictions } = useContext(GlobalContext);

  const { lastidgContent } = useContext(IDGGlobalContext);
  const { VForderData: inProgressCPVFOrder, resetVFOrderWorkflow } = useContext(
    VFGlobalContext,
  );
  const patientGlobal = globalContext.patientGlobal as mumms.patient;

  const classes = useStyles();
  const intl = useIntl();
  const isHospiceProgram = patientGlobal.programSiteName === Hospice;

  const physicianName = attendingPhysician && attendingPhysician.physicianName;
  const attendingPhysicianName =
    physicianName && physicianName !== NoAttendingPhys
      ? physicianName
      : undefined;
  const physicianStatement =
    attendingPhysicianName &&
    intl.formatMessage(defaults.attendingPhysicianMsg, {
      name: attendingPhysicianName as string,
    });

  const [locType, setLocType] = useState<mumms.OptionType | undefined>(
    undefined,
  );

  const [facilityName, setFacilityName] = useState<
    mumms.OptionType | undefined
  >(undefined);
  // we do not show facilityType but we save the first option, was removed later on
  const [facilityType, setFacilityType] = useState<
    mumms.OptionType | undefined
  >(undefined);
  const [facilityCheckOpen, setFacilityCheckOpen] = useState(false);
  const [facilityCheckReason, setFacilityCheckReason] = useState('');

  const tz = moment.tz.guess();
  const siteTimezone = (currentUser && currentUser.siteTimezone) || tz;
  const [date, setDate] = useState<Date | null>(null);
  const siteDate = date && toSiteTz(date, siteTimezone);

  const [orderError, setOrderError] = useState<any>(false);
  const [generalOrderText, setOrderText] = useState<string>(emptyText);
  const [facilitiesOptions, setFacilitiesOptions] = useState<
    mumms.OptionType[] | []
  >([]);

  // LOC and location orders should be future datable. Up to 3 days
  const maxLOCDate = moment(Date.now()).add(3, 'days');

  const referralDate =
    patientGlobal && moment((patientGlobal as mumms.patient).referralDate);
  const referralDateDisplay = referralDate.format('MM/DD/YYYY HH:mm');

  // business rule: eff date should be greater than the verbal date
  const minEffectiveDate = moment(verbalDate).startOf('day');
  const admitError = date && referralDate > moment(date);
  const userCredentials = getUserCredentials(currentUser);

  // on Mount
  useEffect(() => {
    // reset it to default value
    if (orderType !== PSO) {
      dispatch({
        type: SET_ORDER_DISABLED,
        payload: true,
      });
    }

    if (orderType === PSO && orderDisabled) {
      dispatch({
        type: SET_ORDER_DISABLED,
        payload: false,
      });
    }
    return () => {
      // console.log('UNMOUNT LOC ADMIT STATEMENT');
    };
  }, []);

  // on Mount ADMIT Menu, LOC and facility type should default to patientGlobal's current value
  useEffect(() => {
    const currentLOC = patientGlobal?.loc && lOCLongMapping[patientGlobal.loc];
    if (orderType === ADM) {
      const locOption = patientGlobal.loc && {
        label: levelOfCaresMapping[currentLOC],
        id: patientGlobal.loc,
        value: lOCLongMapping[patientGlobal.loc],
      };
      locOption && setLocType(locOption);
      patientGlobal?.facility && getFacilitiesForSiteLOC(currentLOC);
    }
    // on Mount for FC or Facility Change load the facility name with current patientGlobal LOC
    if (orderType === FC) {
      patientGlobal && getFacilitiesForSiteLOC(currentLOC);
    }
    // for LOC default to current Facility
    if (orderType === LOC) {
      patientGlobal?.facility && getFacilitiesForSiteLOC(currentLOC);
    }
  }, []);

  // on Mount Admin Menu
  useEffect(() => {
    if (orderType === ADM || orderType === LOC) {
      dispatch({
        type: GET_LOC_TYPES,
        payload: true,
      });
    }
  }, [orderType]);

  useEffect(() => {
    if (locType) {
      // get called twice for ADMIT
      getFacilitiesForSiteLOC();
    }
  }, [locType]);

  useEffect(() => {
    if (facilityName) {
      const options = (facilityName as any).facilityTypes.map(
        (name: string) => ({
          id: name,
          label: name,
          value: name,
        }),
      );
      // always default to the first options
      setFacilityType(options[0]);
    }
  }, [facilityName]);

  useEffect(() => {
    if (orderType !== ADM) {
      const minDateError = moment(date).diff(moment(minEffectiveDate)) < 0;
      if (minDateError) {
        setOrderError(true);
      } else {
        setOrderError(false);
        setErrorDate(false);
      }
    }
    if (orderType === ADM) {
      const minDateError =
        moment(date).diff(moment(verbalDate).startOf('day')) < 0;
      if (minDateError) {
        setOrderError(true);
      } else {
        setOrderError(false);
        setErrorDate(false);
      }
    }
  }, [verbalDate]);

  /**
   * getFacilitiesForSiteLOC triggered for LOC change
   */

  const getFacilitiesForSiteLOC = async (locName?: string) => {
    // api/levelofcare/facilities?agency=demo1&siteId=17&levelOfCareType=GENERAL_IN_PATIENT
    const facilitiesForLoc = await pim.getFacilitiesForSiteLOC(
      siteId || patientGlobal.siteId.toString(),
      locName || (locType as any)?.value,
      agency,
    );
    // OP-33240: Patient's Home at the top of the location list
    const facilityOptions = facilitiesForLoc.length
      ? homeFacilityFirst<mumms.OptionType>(
          facilitiesForLoc.map((facility: any) => ({
            ...facility,
            label: facility.name,
            value: facility.name,
          })),
        )
      : [];
    setFacilitiesOptions(facilityOptions);
    // dispatch for CheckOrderMenu to consume
    dispatch({
      type: SET_FACILITY_OPTIONS,
      payload: facilityOptions,
    });
  };
  // keyboard input allows you to enter invalid date
  const [errorDate, setErrorDate] = useState<boolean>(false);

  const checkDate = (
    maxDateError: any,
    minDateError: any,
    isInvalidDate: any,
  ) => {
    if (maxDateError || minDateError || isInvalidDate) {
      setErrorDate(true);
    } else {
      setErrorDate(false);
      setOrderError(false);
    }
  };

  const handleDateChange = (selecteddate: any) => {
    const newDate = offsetDate(selecteddate);
    const newMoment = moment(newDate);
    setDate(newDate);
    dispatch({
      type: SET_EFFECTIVE_DATE,
      payload: newDate,
    });

    const isInvalidDate = !newMoment.isValid();
    // ordertype === ADM;
    // minimumDate={verbalDate}
    // maximumDate={Date.now()}
    if (orderType === ADM) {
      const maxDateError = newMoment.diff(moment(Date.now())) > 0;
      const minDateError =
        newMoment.diff(dateToMoment(verbalDate).startOf('day')) < 0;
      checkDate(maxDateError, minDateError, isInvalidDate);
    } else {
      // ordertype !== ADM;
      // minimumDate={minEffectiveDate.toDate()}
      // maximumDate={maxLOCDate}
      const maxDateError = newMoment.diff(maxLOCDate) > 0;
      const minDateError = newMoment.diff(minEffectiveDate) < 0;
      checkDate(maxDateError, minDateError, isInvalidDate);
    }
  };

  const signDate = getLocalOrderTime(currentUser);
  const getADMFormattedMsg = () => {
    if (isHospiceProgram) {
      // only show message if attending physician is PECOS eligible and attending is not the same as ordering
      if (
        attendingPhysicianName &&
        pecosPhysicians &&
        pecosPhysicians?.attPhysician &&
        !pecosPhysicians?.samePhys
      ) {
        return intl.formatMessage(defaults.admitOrderAttPhys, {
          levelOfCare: locType?.label,
          facilityName: facilityName?.label,
          date: formatDateTimeMMDD(date),
          physician: attendingPhysicianName as string,
          userCredentials,
          signDate,
        });
      }
      return intl.formatMessage(defaults.admitOrder, {
        levelOfCare: locType?.label,
        facilityName: facilityName?.label,
        date: formatDateTimeMMDD(date),
        userCredentials,
        signDate,
      });
    } else {
      if (attendingPhysicianName) {
        return intl.formatMessage(defaults.admStatement.admProgramPhys, {
          program: patientGlobal.programSiteName,
          date: formatDateTimeMMDD(date),
          physician: attendingPhysicianName as string,
        });
      }
      return intl.formatMessage(defaults.admStatement.admProgram, {
        program: patientGlobal.programSiteName,
        date: formatDateTimeMMDD(date),
      });
    }
  };

  const handleLocTypeChange = (type?: any) => {
    setLocType(type);
    dispatch({
      type: SET_LOC_TYPE,
      payload: type.id,
    });
  };
  const closeFacilityCheck = () => {
    setFacilityCheckOpen(false);
    setFacilityCheckReason('');
  };

  const handleFacilityChange = (factype?: any) => {
    setFacilityName(factype);
    dispatch({
      type: SET_FACILITY_TYPE,
      payload: factype,
    });
    // Patient's Home is not a real facility, so skip the facility check
    if (factype && factype.isValid === false && !isHomeFacility(factype.id)) {
      setFacilityCheckReason(factype.notValidReason || '');
      setFacilityCheckOpen(true);
    }
  };

  const facilityCheckMessage = facilityCheckReason
    ? intl.formatMessage(defaults.facilityCheckInvalid, {
        notValidReason: facilityCheckReason,
      })
    : intl.formatMessage(defaults.facilityCheckInvalidNoReason);

  const handleOrderTextChange = (event: any) => {
    const { value: nextValue } = event.target;
    setOrderText(nextValue);
  };

  const getLOCendStatement = (type: mumms.OptionType | undefined) =>
    type && lOCStatements[type?.id as string];

  // todo move to utils file
  const getFormattedMsg = (ordertype: mumms.EntityOrderType | null) => {
    const dateText = formatLocDateMMDDYYYY(date);
    switch (ordertype) {
      case LOC: {
        const locOrder = isPhysicianRoleGroup
          ? defaults.locPhysOrder
          : defaults.locOrder;
        return intl.formatMessage(locOrder, {
          levelOfCare: locType?.label,
          patientLOC: patientGlobal.loc,
          facilityName: facilityName?.label,
          facilityType: facilityType?.label,
          date: dateText,
          explanation: getLOCendStatement(locType),
          userCredentials,
          signDate,
        });
      }
      case FC: {
        const facilityChangeOrder = isPhysicianRoleGroup
          ? defaults.facilityChangePhysOrder
          : defaults.facilityChangeOrder;
        return intl.formatMessage(facilityChangeOrder, {
          prevFac: patientGlobal.facility,
          newFac: facilityName?.label,
          date: dateText,
          userCredentials,
          signDate,
        });
      }
      case ADM:
        return getADMFormattedMsg();
      case OTH: {
        const generalOrder = isPhysicianRoleGroup
          ? defaults.generalPhysOrder
          : defaults.generalOrder;
        return intl.formatMessage(generalOrder, {
          orderText: generalOrderText,
          userCredentials,
          signDate,
        });
      }
      case MMD: {
        const medOrder = isPhysicianRoleGroup
          ? defaults.medPhysChangeOrder
          : defaults.medChangeOrder;
        return intl.formatMessage(medOrder, {
          userCredentials,
        });
      }
      case CPF: {
        const textFirst = `${defaults.cpfOrderText}\n   ${Object.values(
          inProgressCPVFOrder,
        ).reduce(
          (seed: any, cpf: any) => seed.concat(`${cpf.frequencyMessage} `),
          '',
        )}`;
        const textSec = intl.formatMessage(defaults.orderVerified, {
          date: dateText,
          userCredentials,
          signDate,
        });
        const cpfText = `${textFirst}\n${textSec}`;
        return cpfText;
      }
      case PSO:
        {
          const payload = intl.formatMessage(defaults.psoPhysOrder, {
            userCredentials,
            signDate: getLocalOrderTime(currentUser),
          });
          return payload;
        }
        break;
      // default is FC
      default:
        return intl.formatMessage(defaults.facilityChangeOrder, {
          prevFac: patientGlobal.facility,
          newFac: facilityType?.label,
          date: dateText,
          userCredentials,
          signDate,
        });
    }
  };

  // get the order statement text
  const getTextContent = () => {
    const payload = getFormattedMsg(orderType as mumms.EntityOrderType);
    dispatchOrders({
      type: SET_ORDER_STATEMENT,
      payload,
    });
  };

  const getFirstWorkflow = () => {
    getTextContent();

    if (isPhysicianRoleGroup) {
      handleSendApproval();
    }
  };
  // trigger get the ADM/LOC/FC/OTH/MMD/CPF statement
  useEffect(() => {
    const textOrder =
      orderType === LOC ||
      orderType === FC ||
      orderType === MMD ||
      orderType === CPF ||
      orderType === OTH ||
      (orderType === PSO && isPhysicianRoleGroup);
    if (createOrder && textOrder && !orderCreated) {
      // setTimeout as we want the approval statement to go first
      setTimeout(() => {
        getFirstWorkflow();
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createOrder]);

  const [admitOverrideOK, setadmitOverrideOK] = useState<boolean>(false);
  useEffect(() => {
    if (orderType === ADM) {
      if (createOrder && !orderCreated) {
        if (isOverrideVerifOrderAgency) {
          if (savedApproval) {
            getTextContent();
            setadmitOverrideOK(true);
          }
        } else {
          setTimeout(() => {
            getTextContent();
          }, 100);
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createOrder, !!savedApproval]);

  // OTH
  useEffect(() => {
    const othPhysCondtion =
      isPhysicianRoleGroup &&
      createOrder &&
      !orderCreated &&
      isOverrideOrder(orderType) &&
      orderStatement;

    if (othPhysCondtion) {
      // setTimeout to make sure we get the correct orderStatement
      setTimeout(() => {
        handleGenericOrder();
      }, 200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createOrder, !!orderStatement]);

  // LOC/FC creation API call
  useEffect(() => {
    const isLOCapi = orderType === LOC || orderType === FC;
    const locCondition =
      createOrder &&
      !orderCreated &&
      isLOCapi &&
      orderStatement &&
      savedApproval;

    if (locCondition) {
      setTimeout(() => {
        // setTimeout to make sure we get the correct orderStatement
        handleLOCCreation();
      }, 400);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createOrder, savedApproval, orderStatement]);
  // handleAdmitPatient API call
  useEffect(() => {
    if (orderType === ADM) {
      if (createOrder && !orderCreated && savedApproval) {
        if (isOverrideVerifOrderAgency) {
          if (admitOverrideOK) {
            handleAdmitPatient();
          }
        } else {
          // setTimeout to make sure we get the correct orderStatement
          setTimeout(() => {
            handleAdmitPatient();
          }, 400);
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createOrder, orderCreated, !!savedApproval, admitOverrideOK]);

  // todo handleCPFCreation API call
  // todo await promises
  useEffect(() => {
    const cpfCondition =
      createOrder &&
      !orderCreated &&
      orderType === CPF &&
      orderStatement &&
      savedApproval;

    if (cpfCondition) {
      // setTimeout to make sure we get the correct orderStatement
      setTimeout(() => {
        handleCPFCreation();
      }, 400);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createOrder, !!savedApproval, orderStatement]);

  const handleAdmitPatient = () => {
    // we were saving everything progressively so the admitPayload was saved to the progress note.
    const admitData = {
      patientnumber: patientGlobal.patientNumber,
      levelOfCareValue: locType?.id,
      levelOfCareDisplayValue: locType?.label,
      facilityId: facilityName?.id ?? patientGlobal.facilityId,
      facilityTypeName:
        facilityType?.label ?? patientGlobal.currentFacilityType,
      admissionDate: date && formatAdmitPostDate(date),
    };
    const certDate = formatDateYYYYMMDD(verbalDate);
    const admitphysicianID = admitPhysician && admitPhysician.id.toString();

    orderStatement &&
      admitphysicianID &&
      admitPatient(
        agency,
        patientGlobal.id.toString(),
        admitphysicianID,
        currentUser.userId,
        certDate,
        admitData,
        orderStatement,
        pecosPhysicians,
      );
  };

  const handleLOCCreation = () => {
    // locType {label: 'Routine Home Care', id: 'RTN', value: 'ROUTINE'}
    const locData = {
      effectiveDate: formatLocDateMMDDYYYY(date),
      dataEntryEndDate: null,
      // LOC or FC
      levelOfCareType:
        locType?.value || lOCLongMapping[(patientGlobal as any)?.loc], // "GENERAL_IN_PATIENT"
      forceUpdate: true,
      facilityId: facilityName?.id,
      roomNumber: null,
      facilityType: facilityType?.label, // "Hospice Inpatient Unit"
      totalCCHours: 8,
    };
    orderStatement &&
      saveLOC(
        agency,
        locData,
        patientGlobal.patientNumber,
        orderStatement,
        orderType as mumms.EntityOrderType,
      );
  };

  const handleCPFCreation = async () => {
    if (rgHash) {
      dispatchOrders({ type: SET_DISPLAY_CONFIRMATION, payload: false });
      const plans = Object.values(inProgressCPVFOrder).map((cpvf: any) => ({
        id: cpvf.sourcePlanId,
        effectiveDate: cpvf.sourceEffectiveDate || cpvf.effectiveDate,
        editPeriod: cpvf.period,
        editFrequency: cpvf.frequency,
        editEffective: cpvf.effectiveDate,
        prn: cpvf.prn,
        editing: false,
        roleGroupId: cpvf.id,
        editDaysOfWeek: cpvf.daysOfWeek,
      }));
      const cpfPlans = plans.map((cpvf: any) =>
        generateNewFrequency(rgHash, cpvf, patientGlobal, agency),
      );
      if (cpfPlans && orderStatement) {
        const completedApproval = await saveCarePlanFrequency(
          agency,
          patientGlobal.patientNumber,
          cpfPlans,
          orderStatement,
          verbalDate,
          admitPhysician,
        );
        if (completedApproval) {
          resetVFOrderWorkflow();
          resetWorkflow();
        }
      }
    }
    // todo
    // else create error message?
  };

  // this order : ADM, LOC, FC, OTH
  const [verbalOrderError, setVerbalOrderError] = useState<boolean>(true);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    // there is an error in the approval process
    const approvalNonPhysError = approvalDateError || !admitPhysician;
    const approvalError =
      isPhysicianRoleGroup && orderType !== ADM ? false : approvalNonPhysError;
    // related to this page order
    const commonOrderError = approvalError || !date || errorDate;
    const FCerror = commonOrderError || !facilityName;
    const LOCerror = commonOrderError || orderError || FCerror || !locType;
    const ADMerror = commonOrderError || orderError || LOCerror || admitError;
    const generalOrderError =
      approvalError || !generalOrderText || generalOrderText === emptyText;
    const frequencyError = approvalError;
    switch (orderType) {
      case ADM:
        return isHospiceProgram
          ? setVerbalOrderError(ADMerror)
          : setVerbalOrderError(commonOrderError);
      case FC:
        setVerbalOrderError(FCerror);
        break;
      case OTH:
        setVerbalOrderError(generalOrderError);
        break;
      case MMD:
        setVerbalOrderError(approvalError);
        break;
      case CPF:
        setVerbalOrderError(frequencyError);
        break;
      case LOC:
        setVerbalOrderError(LOCerror);
        break;
      default:
        setVerbalOrderError(generalOrderError);
        break;
    }
  }, [
    approvalDateError,
    admitPhysician,
    date,
    errorDate,
    generalOrderText,
    facilityName,
    locType,
    inProgressCPVFOrder,
    orderType,
    isPhysicianRoleGroup,
    admitError,
    orderError,
    isHospiceProgram,
  ]);

  useEffect(() => {
    const doNotDisableOrder = orderType === PSO && isPhysicianRoleGroup;
    if (!verbalOrderError && orderDisabled) {
      dispatch({
        type: SET_ORDER_DISABLED,
        payload: false,
      });
    }
    if (verbalOrderError && !orderDisabled && !doNotDisableOrder) {
      dispatch({
        type: SET_ORDER_DISABLED,
        payload: true,
      });
    }
  }, [verbalOrderError, orderDisabled]);

  const getAdmitFacilityName = () => (
    <FacilityPicker
      options={facilitiesOptions}
      handleFacilityChange={handleFacilityChange}
      value={facilityName}
      // @note false || error
      severity={!!facilityName || error}
      maxMenuHeight={pickerDialogHeight}
    />
  );

  // CPF, LOC and FC
  const getEffDatePicker = (ordertype?: mumms.EntityOrderType | null) => {
    const { orderDate, orderDateError } = getMaxOrderDate(
      patientDateRestrictions,
      orderType,
    );
    const errorText = orderDateError
      ? intl.formatMessage(defaults.errorLOCText, {
          date: orderDateError.substring(0, 10),
        })
      : undefined;

    let maximumDate = orderDate;
    // todo CPF eff date time picker?
    if (ordertype === CPF) {
      // agency can override the order verification process
      const overrideCPFEffDateBins: string = window.env
        ?.overrideCPFEffDateBins as string;
      // if true NO verification step
      const overrideCPFEffDateAgency = getOverrideAgency(
        currentUser,
        overrideCPFEffDateBins,
      );
      if (overrideCPFEffDateAgency) {
        maximumDate = undefined;
      } else {
        const includeTwoWeeks = true;
        maximumDate = new Date(
          findMaxEditableDate(patientGlobal, lastidgContent, includeTwoWeeks),
        );
      }
    }
    return (
      <EffDatePicker
        selectedDate={siteDate}
        handleEffDateChange={handleDateChange}
        minimumDate={minEffectiveDate.toDate()} // max of VERBAL_DATE
        maximumDate={maximumDate || undefined}
        width={orderDateError ? 200 : DATE_PICKER_SUGGEST_BOX_WIDTH_PX}
        // @note false || error
        severity={!!date || error}
        error={errorText}
        disabled={!!orderDateError}
      />
    );
  };

  const getStatementUI = (ordertype: mumms.EntityOrderType | null) => {
    switch (ordertype) {
      case ADM:
        return isHospiceProgram ? (
          <>
            <Text id="adm-text-program">
              {defaults.admStatement.admHospProgram}
            </Text>
            <LOCPicker
              options={locTypes || []}
              handleLocTypeChange={handleLocTypeChange}
              value={locType}
              // @note false || error
              severity={!!locType || error}
            />
            <Text id="adm-text-date">{defaults.admStatement.admDate}</Text>
            <div>
              <EffDateTimePicker
                selectedDate={siteDate}
                minimumDate={verbalDate}
                maximumDate={Date.now()}
                handleEffDateChange={handleDateChange}
                width={DATE_TIME_PICKER_SUGGEST_BOX_WIDTH_PX}
                // @note false || error
                severity={!date || admitError ? error : null}
              />
              {admitError && (
                <ErrorText id="helper-text">
                  {intl.formatMessage(defaults.errorAdmitText, {
                    date: referralDateDisplay,
                  })}
                </ErrorText>
              )}
            </div>

            <Text id="adm-text-loc">{defaults.admStatement.admLOC}</Text>
            {getAdmitFacilityName()}
            {attendingPhysicianName && <Text>{physicianStatement}</Text>}
          </>
        ) : (
          <>
            <Text id="adm-text-program">
              {intl.formatMessage(defaults.admStatement.admProgram, {
                program: patientGlobal.programSiteName,
                date: null,
              })}
            </Text>
            <EffDateTimePicker
              selectedDate={siteDate}
              minimumDate={verbalDate}
              maximumDate={Date.now()}
              handleEffDateChange={handleDateChange}
              width={DATE_TIME_PICKER_SUGGEST_BOX_WIDTH_PX}
              // @note false || error
              severity={!!date || error}
            />
            {attendingPhysicianName && <Text>{physicianStatement}</Text>}
          </>
        );
      case LOC:
        return (
          <>
            <Text id="loc-text">{defaults.locStatement.locChange}</Text>
            <Text>
              {defaults.textOrder.from} {patientGlobal.loc}{' '}
              {defaults.textOrder.to}
            </Text>
            <LOCPicker
              options={locTypes || []}
              handleLocTypeChange={handleLocTypeChange}
              value={locType}
              // @note false || error
              severity={!!locType || error}
            />
            <Text>{`. ${defaults.locStatement.locFacChange}`}</Text>
            {getAdmitFacilityName()}
            <Text>{`. ${defaults.locStatement.locDateChange}`}</Text>
            {getEffDatePicker()}
            <Text>{getLOCendStatement(locType)}</Text>
          </>
        );
      case FC: {
        const facText = isPhysicianRoleGroup
          ? defaults.facStatement.facPhysTransfer
          : defaults.facStatement.facTransfer;

        return (
          <>
            <Text id="fc-text">
              {`${facText} ${patientGlobal.facility} ${defaults.textOrder.to} `}
            </Text>
            {getAdmitFacilityName()}
            <Text>{`. ${defaults.facStatement.facDateChange}`}</Text>
            {getEffDatePicker()}
          </>
        );
      }
      case OTH: {
        const generalText = isPhysicianRoleGroup
          ? defaults.generalOrderPhysStatement
          : defaults.generalOrderStatement;
        return (
          <>
            <Text id="oth-text">{generalText}</Text>
            <TextField
              id="general-order-input"
              variant="outlined"
              value={generalOrderText}
              onChange={handleOrderTextChange}
              InputProps={{
                classes: {
                  input: classes.generalOrderInput,
                  root: classes.generalOrderRoot,
                },
              }}
              classes={{
                root: classes.widthGeneralOrder,
              }}
            />
          </>
        );
      }
      case CPF:
        return (
          <>
            <Text id="cpf-text">{defaults.cpfOrderText}</Text>
          </>
        );
      case MMD: {
        const medsText = isPhysicianRoleGroup
          ? defaults.medicationPhysStatement
          : defaults.medicationStatement;
        return (
          <>
            <Text id="mmd-text">{medsText}</Text>
          </>
        );
      }
      default:
        return <></>;
    }
  };

  return (
    <>
      {getStatementUI(orderType as mumms.EntityOrderType)}
      <DialogBox
        open={facilityCheckOpen}
        handleClick={closeFacilityCheck}
        title={defaults.facilityCheckTitle}
        noIcon
        maxWidth="sm"
        disableScrollLock
      >
        <ConfirmModal
          onOKClick={closeFacilityCheck}
          okText={defaults.ok}
          hideCancel
          modalText={facilityCheckMessage}
        />
      </DialogBox>
    </>
  );
};

export default GenericStatement;
